import { useCallback, useRef, useState } from 'react';
import { API_URL, getToken } from '../config';

/**
 * useBattleWordsStream — consumes POST /ai/stream/battle-words (SSE).
 *
 * Events:
 *   { type: "thinking", thinking: "..." }
 *   { type: "word",     word: { hanzi, pinyin, hskLevel, topics, meanings } }
 *   { type: "done" }
 *   { type: "error",    error: "..." }
 *
 * Status: idle → streaming → done | error
 *
 * No unmount-abort (StrictMode-safe). Consumers cancel() explicitly.
 */
export function useBattleWordsStream() {
  const [status, setStatus] = useState('idle');
  const [thinking, setThinking] = useState('');
  const [words, setWords] = useState([]);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);

  const reset = useCallback(() => {
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = null;
    setStatus('idle');
    setThinking('');
    setWords([]);
    setError(null);
  }, []);

  const cancel = useCallback(() => {
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = null;
    setStatus((s) => (s === 'streaming' ? 'idle' : s));
  }, []);

  const submit = useCallback(async (input) => {
    reset();
    setStatus('streaming');
    const controller = new AbortController();
    abortRef.current = controller;
    const token = getToken();

    try {
      const resp = await fetch(`${API_URL}/ai/stream/battle-words`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          Accept: 'text/event-stream',
        },
        body: JSON.stringify(input),
        signal: controller.signal,
      });

      if (!resp.ok) {
        let msg = `HTTP ${resp.status}`;
        try {
          const text = await resp.text();
          if (text) msg = text;
        } catch { /* ignore */ }
        setError(msg);
        setStatus('error');
        return;
      }
      if (!resp.body) {
        setError('Streaming not supported by this browser');
        setStatus('error');
        return;
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buf = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });

        let idx;
        while ((idx = buf.indexOf('\n\n')) !== -1) {
          const rawEvent = buf.slice(0, idx);
          buf = buf.slice(idx + 2);
          const dataLines = rawEvent
            .split('\n')
            .filter((l) => l.startsWith('data:'))
            .map((l) => l.slice(5).trimStart());
          if (dataLines.length === 0) continue;
          let parsed;
          try {
            parsed = JSON.parse(dataLines.join('\n'));
          } catch { continue; }

          if (parsed.type === 'thinking' && parsed.thinking) {
            setThinking((prev) => prev + parsed.thinking);
          } else if (parsed.type === 'word' && parsed.word) {
            setWords((prev) => [...prev, parsed.word]);
          } else if (parsed.type === 'done') {
            setStatus('done');
          } else if (parsed.type === 'error') {
            setError(parsed.error || 'Unknown error');
            setStatus('error');
          }
        }
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
      setError(err.message || 'Request failed');
      setStatus('error');
    } finally {
      abortRef.current = null;
    }
  }, [reset]);

  return { status, thinking, words, error, submit, cancel, reset };
}
